import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Head from '@/components/feature/Head';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredComics = useMemo(() => {
    const filtered = slug ? comics.filter((c) => c.category === slug) : comics;
    return [...filtered].sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'views') return b.views - a.views;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [slug, sortBy]);

  if (!category) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500">未找到该分类</p>
            <Link to="/" className="text-primary-500 text-sm mt-2 inline-block hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: `${category.name}漫画` },
  ];

  const webPageSchema = {
    name: `${category.name}漫画 - 韩漫在线免费`,
    description: `韩漫在线免费为您提供精选${category.name}类韩国漫画，支持下拉式在线阅读。收录${category.count}部优质${category.name}韩漫作品，持续更新中。`,
    url: `https://www.koreancomics.cn/category/${slug}`,
  };

  return (
    <>
      <Head
        title={`${category.name}漫画 - 韩漫在线免费 | 韩国漫画网站`}
        description={`韩漫在线免费为您提供精选${category.name}类韩国漫画，支持下拉式在线阅读。收录${category.count}部优质${category.name}韩漫作品，持续更新中。随时随地享受精彩故事。`}
        keywords={`${category.name}漫画, 韩国漫画, 韩漫在线, ${category.name}韩漫, 免费漫画`}
        canonical={`https://www.koreancomics.cn/category/${slug}`}
      />

      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={webPageSchema} />

      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-16">
          <Breadcrumbs items={breadcrumbItems} />

          <section className="w-full py-10 md:py-16 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                    {category.name}漫画
                  </h1>
                  <p className="text-foreground-500 text-sm mt-1">
                    共 {filteredComics.length} 部作品
                  </p>
                </div>

                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                  {(['latest', 'rating', 'views'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setSortBy(s)}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === s
                          ? 'bg-white text-foreground-900 shadow-sm'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {s === 'latest' ? '最新' : s === 'rating' ? '评分' : '人气'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-foreground-600 hover:text-primary-500 border border-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredComics.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                  <p className="text-foreground-500">该分类暂无漫画</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
                  {filteredComics.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/article/${comic.id}`}
                      className="group block"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                        <img
                          src={comic.cover}
                          alt={`${comic.title} - ${category.name}韩国漫画`}
                          title={`${comic.title} - 韩漫在线免费`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                          <p className="text-white text-xs line-clamp-2">{comic.description}</p>
                        </div>
                        <div className="absolute top-2 left-2">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                            comic.status === 'ongoing'
                              ? 'bg-primary-500 text-white'
                              : 'bg-foreground-700 text-white'
                          }`}>
                            {comic.status === 'ongoing' ? '连载中' : '已完结'}
                          </span>
                        </div>
                        <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm rounded-full px-1.5 py-0.5">
                          <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                          <span className="text-white text-[10px] font-medium">{comic.rating}</span>
                        </div>
                      </div>
                      <h3 className="text-foreground-900 font-semibold text-sm line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {comic.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-foreground-400 text-xs">
                        <span>{comic.author}</span>
                        <span>·</span>
                        <span>{comic.chapters}话</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}